/**
 * O que esta pessoa PODE — para a tela não oferecer o que o servidor vai recusar.
 *
 * ## Isto não decide nada; o servidor decide
 *
 * Quem autoriza é o `PermissaoGuard`, e continuará sendo. Este contexto existe para uma
 * pergunta diferente e menor: **mostrar ou não mostrar o botão**. Um botão que leva a um
 * 403 é pior do que a ausência dele — a pessoa clica, espera, e recebe uma recusa que
 * não explica o que ela deveria ter feito diferente.
 *
 * ## Por que a MESMA função do guard, e não uma regra escrita aqui
 *
 * A alternativa óbvia seria a tela decidir por nome de nível ("Financeiro pode
 * faturar"). Isso é uma segunda regra de permissão, escrita noutro lugar, fadada a
 * divergir da primeira no dia em que alguém criar um nível novo na 10.3. Então `/me`
 * passou a devolver a matriz efetiva (lote F1) e aqui roda `alcanca()` de `@dv/domain` —
 * exatamente a função que o guard chama no servidor.
 *
 * ## Enquanto o perfil não chegou, a resposta é NÃO
 *
 * Falha fechada: durante o carregamento a tela não oferece a ação. O contrário faria o
 * botão piscar na tela de quem não pode — e "sumiu depois de eu ver" é pior do que
 * "nunca apareceu".
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { alcanca, temMarcador, veValoresFinanceiros, type Acao, type Marcador, type Modulo, type NivelDeAcesso } from '@dv/domain'
import type { Perfil } from '@dv/contracts'

const SEM_NIVEL: NivelDeAcesso = { nome: 'sessão pendente', permissoes: {} }

const Contexto = createContext<NivelDeAcesso>(SEM_NIVEL)

export function ProvedorDePermissoes({
  perfil,
  children,
}: {
  perfil: Perfil | null
  children: ReactNode
}) {
  const nivel = useMemo<NivelDeAcesso>(() => {
    if (perfil === null) return SEM_NIVEL

    /*
      A lista pode não vir, e a resposta a isso é "nenhuma permissão" — nunca uma
      exceção. `/me` é lido pelo SHELL: um `.map` de `undefined` aqui derruba a
      aplicação inteira em tela branca, e derruba por causa de um campo que só governa
      quais botões aparecem. Uma API mais antiga (ou um dublê desatualizado) tem de
      resultar em menos botões, não em nenhuma tela.
    */
    const celulas = Array.isArray(perfil.permissoes) ? perfil.permissoes : []
    const permissoes: NivelDeAcesso['permissoes'] = Object.fromEntries(
      celulas.map((c) => [
        c.modulo,
        {
          acao: c.acao,
          podeExcluir: c.podeExcluir,
          podeAprovar: c.podeAprovar,
          podeExportar: c.podeExportar,
          escopoCarteira: c.escopoCarteira,
          veValoresFinanceiros: c.veValoresFinanceiros,
        },
      ]),
    )
    return { nome: perfil.nivelAcessoNome, permissoes }
  }, [perfil])

  return <Contexto.Provider value={nivel}>{children}</Contexto.Provider>
}

/** `pode('faturamento', 'criar')` — a escada é cumulativa, como no servidor. */
export function usePode(): (modulo: Modulo, acao: Acao) => boolean {
  const nivel = useContext(Contexto)
  return useMemo(() => (modulo: Modulo, acao: Acao) => alcanca(nivel, modulo, acao), [nivel])
}

/** A mesma régua do backend para excluir, aprovar e exportar. */
export function useMarcador(): (modulo: Modulo, marcador: Marcador) => boolean {
  const nivel = useContext(Contexto)
  return useMemo(() => (modulo: Modulo, marcador: Marcador) => temMarcador(nivel, modulo, marcador), [nivel])
}

/**
 * O nível inteiro, para quem precisa da mesma conta da API sobre ele (a matriz 2.3 por nível,
 * DS-637). Enquanto o perfil não chegou, é o nível sem permissão nenhuma: falha fechada.
 */
export function useNivelDeAcesso(): NivelDeAcesso {
  return useContext(Contexto)
}

/** Mesma flag financeira que protege as respostas do módulo. */
export function useValoresFinanceiros(): (modulo: Modulo) => boolean {
  const nivel = useContext(Contexto)
  return useMemo(() => (modulo: Modulo) => veValoresFinanceiros(nivel, modulo), [nivel])
}
